import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/** A library never keeps the previous account's files or pending actions. */
export function useLibraryIdentity() {
  const [userId, setUserId] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [generation, setGeneration] = useState(0);
  const identityEpoch = useRef(0);
  const owner = useRef<string | null>(null);
  useEffect(() => {
    let alive = true, changed = false;
    const apply = (id: string | null) => {
      if (!alive) return;
      owner.current = id; setUserId(id); setReady(true); setError(false);
    };
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (owner.current !== (session?.user.id ?? null)) { identityEpoch.current++; setGeneration(identityEpoch.current); }
      changed = true; apply(session?.user.id ?? null);
    });
    const timer = window.setTimeout(() => {
      if (alive && !changed) { setError(true); setReady(true); }
    }, 12000);
    void supabase.auth.getSession().then(({ data, error: cause }) => {
      if (!alive || changed) return;
      if (cause) throw cause;
      apply(data.session?.user.id ?? null);
    }).catch(cause => {
      if (alive && !changed) { console.error("[Library] session failed", cause); setError(true); setReady(true); }
    }).finally(() => window.clearTimeout(timer));
    const lifecycle = identityEpoch;
    return () => { alive = false; lifecycle.current++; owner.current = null; window.clearTimeout(timer); subscription.unsubscribe(); };
  }, [attempt]);
  const retry = useCallback(() => { setReady(false); setError(false); setAttempt(value => value + 1); }, []);
  const assertCurrent = useCallback(async () => {
    const expected = userId;
    const epoch = identityEpoch.current;
    if (!expected || owner.current !== expected) throw new Error("Library session changed");
    const { data, error: cause } = await supabase.auth.getSession();
    if (cause || data.session?.user.id !== expected || owner.current !== expected || epoch !== identityEpoch.current) throw new Error("Library session changed");
  }, [userId]);
  return { userId, ready, error, assertCurrent, retry, generation };
}
